'use client';

/**
 * ProgressiveForm — V3 migration.
 *
 * Multi-step contact form rendered inline in place of
 * ContactMethodSelector when the user picks "Detailed Request". The V2
 * version used Flowbite TextInput/Select with hardcoded gray-700/800
 * chrome and bg-sky-500/bg-green-600 buttons. Migrated to plain native
 * fields styled by the .contact-us-page rule in globals.scss, with
 * .sum-cta-primary / .sum-cta-ghost CTAs and V3 surface tokens for the
 * card chrome.
 */

import { Alert } from 'flowbite-react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { HiArrowLeft, HiArrowRight, HiCheck } from 'react-icons/hi';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import React, { useCallback, useState } from 'react';

type Props = {
  lang: Locale;
  onBack: () => void;
};

type FormStep = 1 | 2 | 3 | 4;

interface FormData {
  name: string;
  email: string;
  requestSubject: string;
  country: string;
  message: string;
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 500,
  color: '#B4C1D8',
  marginBottom: 6,
  letterSpacing: '-0.01em',
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
  color: '#F5F7FA',
  fontSize: 15,
  fontFamily: 'inherit',
  outline: 'none',
};

const ProgressiveFormContent = ({ lang, onBack }: Props) => {
  const { t } = useTranslation(lang);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [currentStep, setCurrentStep] = useState<FormStep>(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    requestSubject: '',
    country: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const totalSteps = 4;

  const handleReCaptchaVerify = useCallback(async () => {
    try {
      if (!executeRecaptcha) {
        console.warn('reCAPTCHA not ready');
        return null;
      }
      return await executeRecaptcha('contact_form');
    } catch (e) {
      console.error('reCAPTCHA error:', e);
      return null;
    }
  }, [executeRecaptcha]);

  const validateStep = (step: FormStep): boolean => {
    setError('');

    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          setError(t('ContactUs/Progressive/name_required'));
          return false;
        }
        if (formData.name.length < 2) {
          setError(t('ContactUs/Progressive/name_too_short'));
          return false;
        }
        break;
      case 2: {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
          setError(t('ContactUs/Progressive/email_required'));
          return false;
        }
        if (!emailRegex.test(formData.email)) {
          setError(t('ContactUs/Section1/key11'));
          return false;
        }
        break;
      }
      case 3:
        if (!formData.requestSubject.trim()) {
          setError(t('ContactUs/Progressive/subject_required'));
          return false;
        }
        break;
      case 4:
        if (!formData.message.trim()) {
          setError(t('ContactUs/Progressive/message_required'));
          return false;
        }
        if (formData.message.length < 10) {
          setError(t('ContactUs/Progressive/message_too_short'));
          return false;
        }
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps) as FormStep);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1) as FormStep);
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsLoading(true);
    setError('');

    try {
      const token = await handleReCaptchaVerify();
      if (!token) {
        setError(t('ContactUs/Progressive/recaptcha_error'));
        return;
      }

      const submissionData = {
        ...formData,
        message: `=== Progressive Contact Form ===
Name: ${formData.name}
Email: ${formData.email}
Country: ${formData.country}
Request Subject: ${formData.requestSubject}
============================

Original Message:
${formData.message}`,
        recaptcha_token: token,
      };

      const response = await fetch('/sum/api/contact-us', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        throw new Error('Failed to send message');
      }
    } catch {
      setError(t('ContactUs/Progressive/error_message'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: 999,
            background: 'rgba(34,211,238,0.1)',
            border: '1px solid rgba(34,211,238,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 22px',
          }}
        >
          <HiCheck style={{ width: 36, height: 36, color: '#22D3EE' }} />
        </div>
        <h3
          style={{
            fontSize: 'clamp(22px, 2.4vw, 28px)',
            fontWeight: 700,
            color: '#F5F7FA',
            letterSpacing: '-0.01em',
            margin: '0 0 10px',
          }}
        >
          {t('ContactUs/Progressive/success_title')}
        </h3>
        <p style={{ color: '#B4C1D8', fontSize: 15, lineHeight: 1.5, margin: '0 0 24px' }}>
          {t('ContactUs/Progressive/success_message')}
        </p>
        <button onClick={onBack} className="sum-cta-primary">
          {t('ContactUs/Progressive/back_to_options')}
        </button>
      </div>
    );
  }

  const stepHeading = (title: string, subtitle: string) => (
    <div style={{ textAlign: 'center', marginBottom: 22 }}>
      <h3
        style={{
          fontSize: 'clamp(18px, 2vw, 22px)',
          fontWeight: 600,
          color: '#F5F7FA',
          letterSpacing: '-0.01em',
          margin: '0 0 6px',
        }}
      >
        {title}
      </h3>
      <p style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.5, margin: 0 }}>{subtitle}</p>
    </div>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            {stepHeading(t('ContactUs/Progressive/step1_title'), t('ContactUs/Progressive/step1_subtitle'))}
            <input
              type="text"
              placeholder={t('ContactUs/Progressive/name_placeholder')}
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              style={fieldStyle}
              autoFocus
            />
          </div>
        );

      case 2:
        return (
          <div>
            {stepHeading(t('ContactUs/Progressive/step2_title'), t('ContactUs/Progressive/step2_subtitle'))}
            <input
              type="email"
              placeholder={t('ContactUs/Progressive/email_placeholder')}
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              style={fieldStyle}
              autoFocus
            />
          </div>
        );

      case 3:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {stepHeading(t('ContactUs/Progressive/step3_title'), t('ContactUs/Progressive/step3_subtitle'))}
            <select
              value={formData.requestSubject}
              onChange={(e) => setFormData((prev) => ({ ...prev, requestSubject: e.target.value }))}
              style={fieldStyle}
            >
              <option value="">{t('ContactUs/Section1/subject_placeholder')}</option>
              <option value="Demo Request">{t('ContactUs/Section1/subject_demo')}</option>
              <option value="Technical Support">{t('ContactUs/Section1/subject_support')}</option>
              <option value="Sales Inquiry">{t('ContactUs/Section1/subject_sales')}</option>
              <option value="General Question">{t('ContactUs/Section1/subject_general')}</option>
              <option value="Other">{t('ContactUs/Section1/subject_other')}</option>
            </select>

            <select
              value={formData.country}
              onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))}
              style={fieldStyle}
            >
              <option value="">{t('ContactUs/Section1/country_placeholder')}</option>
              <option value="Switzerland">Switzerland</option>
              <option value="France">France</option>
              <option value="Germany">Germany</option>
              <option value="United States">United States</option>
              <option value="Other">{t('ContactUs/Section1/country_other')}</option>
            </select>
          </div>
        );

      case 4:
        return (
          <div>
            {stepHeading(t('ContactUs/Progressive/step4_title'), t('ContactUs/Progressive/step4_subtitle'))}
            <textarea
              rows={5}
              placeholder={t('ContactUs/Progressive/message_placeholder')}
              value={formData.message}
              onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
              style={{ ...fieldStyle, resize: 'vertical', minHeight: 120 }}
              autoFocus
            />
          </div>
        );

      default:
        return null;
    }
  };

  const isLastStep = currentStep === totalSteps;

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      {/* Progress meta + bar */}
      <div style={{ marginBottom: 22 }}>
        <div
          className="mono"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            color: '#6B7593',
            marginBottom: 8,
          }}
        >
          <span>
            {t('ContactUs/Progressive/step')} {currentStep} {t('ContactUs/Progressive/of')} {totalSteps}
          </span>
          <span style={{ color: '#22D3EE' }}>{Math.round((currentStep / totalSteps) * 100)}%</span>
        </div>
        <div
          style={{
            width: '100%',
            height: 4,
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 999,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${(currentStep / totalSteps) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #22D3EE 0%, #7FF4FF 100%)',
              borderRadius: 999,
              transition: 'width 0.4s ease-out',
            }}
          />
        </div>
      </div>

      {/* Step card */}
      <div
        style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          padding: 'clamp(20px, 3vw, 32px)',
        }}
      >
        {error && (
          <Alert color="failure" className="mb-5 text-sm">
            {error}
          </Alert>
        )}

        {renderStep()}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            marginTop: 26,
          }}
        >
          <button
            type="button"
            onClick={currentStep === 1 ? onBack : handlePrevious}
            className="sum-cta-ghost"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <HiArrowLeft style={{ width: 16, height: 16 }} />
            {currentStep === 1 ? t('ContactUs/Progressive/back_to_options') : t('ContactUs/Progressive/previous')}
          </button>

          {isLastStep ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading}
              className="sum-cta-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading ? '…' : t('ContactUs/Progressive/send')}
              <HiCheck style={{ width: 16, height: 16 }} />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              className="sum-cta-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              {t('ContactUs/Progressive/next')}
              <HiArrowRight style={{ width: 16, height: 16 }} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProgressiveForm = ({ lang, onBack }: Props) => {
  return (
    <GoogleReCaptchaProvider useEnterprise={true} reCaptchaKey={process.env.NEXT_PUBLIC_SITEKEY_V3 as string}>
      <ProgressiveFormContent lang={lang} onBack={onBack} />
    </GoogleReCaptchaProvider>
  );
};

export default ProgressiveForm;
